import { useState } from 'react';
import { Layout } from '@/components/ui/Layout';
import { Button } from '@/components/ui/Button';
import { 
  BarChart2, 
  Brain, 
  Users, 
  AlertTriangle, 
  TrendingUp,
  Scale,
  HelpCircle,
  Shield,
  GitMerge,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Download,
  Save
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

interface Section {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  expanded: boolean;
  completed: boolean;
  progress: number;
}

const initialSections: Section[] = [
  {
    id: 'roadmap',
    title: 'AI Adoption Roadmap',
    description: 'Create a step-by-step AI implementation plan based on your business goals and timeline.',
    icon: Brain,
    expanded: false,
    completed: false,
    progress: 0
  },
  {
    id: 'impact',
    title: 'AI Business Impact Analysis',
    description: 'Calculate potential ROI and productivity gains from AI implementation.',
    icon: BarChart2,
    expanded: false,
    completed: false,
    progress: 0
  },
  {
    id: 'readiness',
    title: 'Workforce AI Readiness & Training',
    description: 'Assess your team\'s AI skills and identify training needs.',
    icon: Users,
    expanded: false,
    completed: false,
    progress: 0
  },
  {
    id: 'risk',
    title: 'AI Adoption Risk Management',
    description: 'Identify and mitigate potential risks in AI adoption.',
    icon: AlertTriangle,
    expanded: false,
    completed: false,
    progress: 0
  },
  {
    id: 'performance',
    title: 'AI Performance Tracking & Optimization',
    description: 'Monitor and optimize AI adoption metrics and effectiveness.',
    icon: TrendingUp,
    expanded: false,
    completed: false,
    progress: 0
  },
  {
    id: 'benchmarking',
    title: 'Competitive & Industry Benchmarking',
    description: 'Compare your AI adoption progress against industry standards.',
    icon: Scale,
    expanded: false,
    completed: false,
    progress: 0
  },
  {
    id: 'insights',
    title: 'Decision Support & Executive Insights',
    description: 'Get AI-powered recommendations for strategic decisions.',
    icon: HelpCircle,
    expanded: false,
    completed: false,
    progress: 0
  },
  {
    id: 'ethics',
    title: 'AI Ethics & Governance Tools',
    description: 'Ensure responsible AI use with compliance and fairness checks.',
    icon: Shield,
    expanded: false,
    completed: false,
    progress: 0
  },
  {
    id: 'integration',
    title: 'Integration & Scalability Support',
    description: 'Plan for seamless AI integration and future scalability.',
    icon: GitMerge,
    expanded: false,
    completed: false,
    progress: 0
  }
];

export function Strategy() {
  const [sections, setSections] = useState<Section[]>(initialSections);

  const toggleSection = (id: string) => {
    setSections(sections.map(section => 
      section.id === id ? { ...section, expanded: !section.expanded } : section
    ));
  };

  const overallProgress = Math.round(
    (sections.reduce((acc, section) => acc + section.progress, 0) / sections.length)
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
              AI Strategy Workbook
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Interactive tools to guide your AI adoption journey
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Overall Progress</p>
              <p className="text-2xl font-semibold text-blue-600 dark:text-blue-400">{overallProgress}%</p>
            </div>
            <Button variant="outline">
              <Save className="h-4 w-4 mr-2" />
              Save Progress
            </Button>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </Button>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {sections.map(section => (
            <div 
              key={section.id}
              className={cn(
                "bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden transition-all duration-200",
                section.expanded && "ring-2 ring-blue-500"
              )}
            >
              <div 
                className="p-6 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                onClick={() => toggleSection(section.id)}
              >
                <div className="flex items-center space-x-4">
                  <div className={cn(
                    "p-2 rounded-lg",
                    section.completed 
                      ? "bg-green-100 dark:bg-green-900/50" 
                      : "bg-blue-100 dark:bg-blue-900/50"
                  )}>
                    <section.icon className={cn(
                      "h-6 w-6",
                      section.completed 
                        ? "text-green-600 dark:text-green-400" 
                        : "text-blue-600 dark:text-blue-400"
                    )} />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {section.title}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {section.description}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  {section.progress > 0 && (
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {section.progress}%
                    </div>
                  )}
                  {section.expanded ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </div>

              {section.expanded && (
                <div className="border-t border-gray-200 dark:border-gray-700 p-6">
                  <div className="space-y-4">
                    <p className="text-gray-700 dark:text-gray-300">
                      This interactive tool will guide you through the process of {section.title.toLowerCase()}.
                    </p>
                    <Button>
                      Start Assessment
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}